import { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { formatVolume, muscleKey, volumeReadsZero } from "@heraion/shared";

import { ChartCappedNote } from "../charts/ChartCappedNote";
import { Chip } from "../primitives/Chip";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import type { UiTheme } from "../theme";
import { weightUnitLabels } from "../weightUnitLabels";
import { WidgetTitleRow } from "./InfoPill";
import type { TagWeeks } from "./useTagVolumeRecords";
import { WeekBarsCard } from "./WeekBarsCard";

// Chips in wrapped rows: the chip's own 36 + this vertical slop is the 44 on
// native (on web the chip's height is the 44, `webTapFloor`), and the row gap
// (s3 = 8 = the two 4px halos) keeps two rows' touch zones from overlapping.
const CHIP_SLOP = { top: 4, bottom: 4 } as const;
// The printed number on a bar carries no unit: the header does.
const NO_UNIT = { kg: "", lb: "" } as const;

export type ProgressRecordsCardProps = {
  /** null until the chart workouts arrive (useTagVolumeRecords). */
  tagWeeks: TagWeeks | null;
  chartError: string | null;
  displayUnit: "kg" | "lb";
  locale: string;
  theme: UiTheme;
  // Fires when the card mounts. Progress mounts it only while its widget is
  // on and open (PG-57, the collapse moved to the layout), so the host still
  // starts the (heavy) chart-workouts fetch only once the card is actually
  // seen (PG-39). `useChartWorkouts`'s own `startedRef` is the one-shot
  // latch that keeps the fetch itself from re-firing, so the host's
  // flag-flip on every call is safely idempotent.
  onExpand?: () => void;
};

// Volume-by-tag card, a Progress widget. Shared between mobile and web-rnw
// (lift/progress-tag-volume-web); the host supplies `tagWeeks` through the
// shared `useTagVolumeRecords`. PG-64 fetta 2: ONE chart, of the muscle picked
// in the chips (the most trained by default), its volume per week on the
// PG-63 bars, zero-based. Chips of muscle tags only, one per catalog muscle.
export function ProgressRecordsCard({
  tagWeeks,
  chartError,
  displayUnit,
  locale,
  theme,
  onExpand,
}: ProgressRecordsCardProps) {
  const { t } = useTranslation();
  // The translated unit word for every weight below (CN-26 ondata 2).
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    onExpand?.();
  }, [onExpand]);

  // Most trained first; a muscle whose weeks would print «0» is not offered.
  const muscles = useMemo(
    () =>
      (tagWeeks?.series ?? [])
        .map((s) => ({ ...s, total: s.weeks.reduce((a, v) => a + v, 0) }))
        .filter((s) => !volumeReadsZero(s.total, displayUnit))
        .sort((a, b) => b.total - a.total),
    [tagWeeks, displayUnit],
  );
  const shown = muscles.find((m) => m.muscle === picked) ?? muscles[0];

  return (
    <View style={styles.card}>
      <WidgetTitleRow
        theme={theme}
        title={
          <Text style={styles.cardTitle}>
            {t("mobile.progress.tagVolumeTitle")}
          </Text>
        }
      />
      <Text style={styles.cardHelp}>
        {t("mobile.progress.tagVolumeWeeksHelp", {
          defaultValue: "One muscle's training volume per week. Pick the muscle.",
        })}
      </Text>
      <View style={styles.loadingBlock} accessibilityLiveRegion="polite">
        {tagWeeks === null && chartError === null ? (
          <LoadingIndicator theme={theme} />
        ) : chartError !== null ? (
          <Text style={styles.chartError}>
            {t("mobile.progress.chartLoadFailed")}
          </Text>
        ) : shown === undefined || tagWeeks === null ? (
          <Text style={styles.emptyText}>{t("mobile.progress.noTags")}</Text>
        ) : (
          <>
            {muscles.length > 1 ? (
              <View style={styles.chipRow} accessibilityRole="radiogroup">
                {muscles.map((m) => (
                  <Chip
                    key={m.muscle}
                    theme={theme}
                    role="radio"
                    label={t(muscleKey(m.muscle))}
                    selected={m.muscle === shown.muscle}
                    onPress={() => setPicked(m.muscle)}
                    hitSlop={CHIP_SLOP}
                  />
                ))}
              </View>
            ) : null}
            <WeekBarsCard
              theme={theme}
              title={t(muscleKey(shown.muscle))}
              values={shown.weeks}
              nowMs={tagWeeks.nowMs}
              weekStartDay={tagWeeks.weekStartDay}
              locale={locale}
              valueFormat={(y) =>
                formatVolume(y, displayUnit, locale, NO_UNIT).trim()
              }
              readoutFormat={(y) => formatVolume(y, displayUnit, locale, unitLabels)}
              partialLast
            />
            {tagWeeks.capped ? <ChartCappedNote theme={theme} /> : null}
          </>
        )}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    cardTitle: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    // Token-composed floor (spacing.s8 × 3 = 144), not a literal pixel value:
    // keeps the card from visibly jumping when the spinner/error give way to
    // the loaded chart. Centers the spinner/error states.
    loadingBlock: {
      minHeight: s.s8 * 3,
      justifyContent: "center",
      gap: s.s3,
    },
    cardHelp: helpTextStyle(theme),
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      paddingVertical: s.s3,
    },
    chartError: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    chipRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: s.s2,
      rowGap: s.s3,
    },
  });
}
